import { useState, useEffect, useRef } from "react";
import React from "react";
import Icon from "../components/Icon";
import ConfirmDelete from "../components/ConfirmDelete";
import UnsavedChangesGuard from "../components/UnsavedChangesGuard";
import { fmt, calcEstimateTotal } from "../utils";

export const LEAD_STAGES = ["new","contacted","site-visit","design3d","estimate-sent","negotiation","won","lost","warranty","project-started","project-complete"];

export const STAGE_COLORS = {new:"#3b82f6",contacted:"#8b5cf6","site-visit":"#6366f1",design3d:"#0891b2","estimate-sent":"#f59e0b",negotiation:"#f97316",won:"#16a34a",lost:"#ef4444",warranty:"#6b7280","project-started":"#0d9488","project-complete":"#15803d"};

function getLeadValue(lead, estimates) {
  if (!estimates?.length || !lead.customerId) return lead.value || 0;
  const customerEsts = estimates
    .filter(e => e.customerId === lead.customerId)
    .map(e => ({ ...e, _total: calcEstimateTotal(e).total }));
  const approved = customerEsts.filter(e => e.status === "approved")
    .sort((a,b) => (b.createdAt||"").localeCompare(a.createdAt||""));
  if (approved.length) return approved[0]._total;
  const sent = customerEsts.filter(e => e.status === "sent")
    .sort((a,b) => (b.createdAt||"").localeCompare(a.createdAt||""));
  if (sent.length) return sent[0]._total;
  return lead.value || 0;
}

function Pipeline({ t, lang, leads, addLead, updateLead, deleteLead, customers, estimates, setPage, onPreselectEstimate, onNavigateToCustomerComms }) {
  const [view, setView] = useState("board");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const touchDragRef = useRef({ id: null, clone: null, overStage: null, moveHandler: null });
  const [isDirty, setIsDirty] = useState(false);
  const [guard, setGuard] = useState(null);
  useEffect(() => { setIsDirty(false); setGuard(null); }, [modal]);
  const tryClose = (closeFn, saveFn) => { if (!isDirty) { closeFn(); return; } setGuard({ close: closeFn, save: saveFn }); };

  const activePipeline = leads.filter(l=>!["won","lost"].includes(l.stage)).reduce((a,l)=>a+getLeadValue(l,estimates),0);
  const wonTotal = leads.filter(l=>l.stage==="won").reduce((a,l)=>a+getLeadValue(l,estimates),0);
  const getCustomerName = (id) => { const c=customers.find(c=>c.id===id); return c?`${c.firstName} ${c.lastName}`:""; };

  const openAdd = () => { setForm({name:"",customerId:"",value:"",stage:"new",service:"Hardscape",date:"",notes:""}); setModal("add"); };
  const openEdit = (l) => { setForm({...l}); setModal("edit"); };
  const save = async () => {
    const data = {...form, value:+form.value||0};
    if (modal==="add") await addLead(data);
    else await updateLead(form.id, data);
    setModal(null);
  };

  const onDrop = async (stage) => {
    if (dragId) { const lead=leads.find(l=>l.id===dragId); if(lead) await updateLead(dragId,{...lead,stage}); setDragId(null); setOverStage(null); }
  };

  const onTouchStart = (e, leadId) => {
    const touch = e.touches[0];
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const clone = card.cloneNode(true);
    clone.className = "kanban-touch-clone";
    clone.style.width = rect.width + "px";
    clone.style.left = (touch.clientX - rect.width / 2) + "px";
    clone.style.top = (touch.clientY - rect.height - 10) + "px";
    document.body.appendChild(clone);
    setDragId(leadId);
    touchDragRef.current = { id: leadId, clone, overStage: null, moveHandler: null };
    const moveHandler = (ev) => {
      ev.preventDefault();
      const t = ev.touches[0];
      const r = touchDragRef.current;
      if (!r.clone) return;
      r.clone.style.left = (t.clientX - rect.width / 2) + "px";
      r.clone.style.top = (t.clientY - rect.height - 10) + "px";
      r.clone.style.display = "none";
      const el = document.elementFromPoint(t.clientX, t.clientY);
      r.clone.style.display = "";
      const dropZone = el?.closest("[data-stage]");
      const stage = dropZone?.dataset?.stage || null;
      if (stage !== r.overStage) { r.overStage = stage; setOverStage(stage); }
    };
    touchDragRef.current.moveHandler = moveHandler;
    document.addEventListener("touchmove", moveHandler, { passive: false });
  };

  const onTouchEnd = async () => {
    const ref = touchDragRef.current;
    if (ref.moveHandler) document.removeEventListener("touchmove", ref.moveHandler);
    if (ref.clone) { document.body.removeChild(ref.clone); }
    const { id: touchLeadId, overStage: targetStage } = ref;
    touchDragRef.current = { id: null, clone: null, overStage: null, moveHandler: null };
    setOverStage(null);
    setDragId(null);
    if (targetStage && touchLeadId) {
      const lead = leads.find(l => l.id === touchLeadId);
      if (lead && lead.stage !== targetStage) await updateLead(touchLeadId, { ...lead, stage: targetStage });
    }
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-row">
          <div><h1>{t.leads.title}</h1><p>{leads.length} {t.leads.subtitle}</p></div>
          <div style={{display:"flex",gap:8}}>
            <div className="view-toggle">
              <button className={`view-toggle-btn${view==="board"?" active":""}`} onClick={()=>setView("board")}><Icon name="board" size={13}/>{t.leads.board}</button>
              <button className={`view-toggle-btn${view==="list"?" active":""}`} onClick={()=>setView("list")}><Icon name="list" size={13}/>{t.leads.list}</button>
            </div>
            <button className="btn btn-primary" onClick={openAdd}><Icon name="plus" size={14}/>{t.leads.addBtn}</button>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="card" style={{marginBottom:20}}>
          <div className="pipeline-stats">
            {[{label:t.leads.activePipeline,value:fmt(activePipeline),cls:""},{label:t.leads.won,value:fmt(wonTotal),cls:" green"},{label:t.leads.totalLeads,value:leads.length,cls:""}].map((s,i)=>(
              <div key={i}><div className="pipeline-stat-label">{s.label}</div><div className={`pipeline-stat-value${s.cls}`}>{s.value}</div></div>
            ))}
          </div>
          <div style={{padding:16}}/>
        </div>
        {view==="board"?(
          <div className="kanban" style={{overflowX:"auto"}}>
            {LEAD_STAGES.map(stage=>{
              const stageLeads=leads.filter(l=>l.stage===stage);
              const stageTotal=stageLeads.reduce((a,l)=>a+getLeadValue(l,estimates),0);
              return (
                <div key={stage} className="kanban-col">
                  <div className="kanban-col-header" style={["warranty","project-started","project-complete"].includes(stage)?{background:STAGE_COLORS[stage]+"22",borderColor:STAGE_COLORS[stage]+"55"}:undefined}>
                    <div className="kanban-col-title"><div className="kanban-col-dot" style={{background:STAGE_COLORS[stage]}}/>{t.leads.stages[stage]}</div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>{stageTotal>0&&<span className="col-total">{fmt(stageTotal)}</span>}<span className="kanban-col-count">{stageLeads.length}</span></div>
                  </div>
                  <div className={`kanban-drop-zone${overStage===stage?" over":""}`} data-stage={stage} onDragOver={e=>{e.preventDefault();setOverStage(stage);}} onDragLeave={()=>setOverStage(null)} onDrop={()=>onDrop(stage)}>
                    {stageLeads.length===0&&<div className="drop-hint">Drop here</div>}
                    {stageLeads.map(l=>{
                      const effectiveValue = getLeadValue(l, estimates);
                      const fromEstimate = effectiveValue !== (l.value || 0);
                      return (
                      <div key={l.id} className="kanban-card" draggable onDragStart={()=>setDragId(l.id)} onTouchStart={e=>onTouchStart(e,l.id)} onTouchEnd={onTouchEnd}>
                        <div className="kanban-card-name">{l.name}</div>
                        <div className="kanban-card-customer">{getCustomerName(l.customerId)}</div>
                        {l.service&&<div style={{fontSize:11,color:"var(--text-3)",marginBottom:6}}>{l.service}</div>}
                        <div className="kanban-card-meta"><span className="kanban-value">{fmt(effectiveValue)}{fromEstimate&&<span style={{fontSize:9,opacity:0.6,marginLeft:3}}>est.</span>}</span>{l.date&&<span className="kanban-date"><Icon name="calendar" size={10}/>{l.date?.slice(5)}</span>}</div>
                        <div style={{display:"flex",gap:4,marginTop:8,flexWrap:"wrap",alignItems:"center"}}>
                          {/* Contextual next-step */}
                          {l.stage==="new"&&(
                            <button className="btn btn-secondary btn-sm" onClick={e=>{e.stopPropagation();updateLead(l.id,{...l,stage:"site-visit"});}} style={{fontSize:11,padding:"3px 8px",whiteSpace:"nowrap"}}>{lang==="fr"?"Visite":"Site Visit"}</button>
                          )}
                          {l.stage==="site-visit"&&l.customerId&&onPreselectEstimate&&(
                            <button className="btn btn-secondary btn-sm" onClick={e=>{e.stopPropagation();onPreselectEstimate(l.customerId);}} style={{fontSize:11,padding:"3px 8px",whiteSpace:"nowrap"}}>{lang==="fr"?"Soumission":"Estimate"}</button>
                          )}
                          {l.stage==="estimate-sent"&&l.customerId&&onNavigateToCustomerComms&&(
                            <button className="btn btn-secondary btn-sm" onClick={e=>{e.stopPropagation();onNavigateToCustomerComms(l.customerId);}} style={{fontSize:11,padding:"3px 8px",whiteSpace:"nowrap"}}>{lang==="fr"?"Relancer":"Follow Up"}</button>
                          )}
                          {l.stage==="won"&&setPage&&(
                            <button className="btn btn-secondary btn-sm" onClick={e=>{e.stopPropagation();setPage("jobs");}} style={{fontSize:11,padding:"3px 8px",whiteSpace:"nowrap"}}>{lang==="fr"?"Démarrer":"Start Job"}</button>
                          )}
                          <button className="btn-icon" title={t.common.edit} onClick={()=>openEdit(l)}><Icon name="edit" size={12}/></button>
                          <button className="btn-icon danger" title={t.common.delete} onClick={()=>setConfirmDelete(l)}><Icon name="trash" size={12}/></button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ):(
          <div className="card">
            {leads.map(l=>{
              const effectiveValue = getLeadValue(l, estimates);
              const fromEstimate = effectiveValue !== (l.value || 0);
              return (
              <div key={l.id} className="list-row">
                <div style={{flex:1}}><div style={{fontWeight:600,fontSize:14}}>{l.name}</div><div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>{getCustomerName(l.customerId)} · {l.service}</div></div>
                <span className="badge" style={{background:STAGE_COLORS[l.stage]+"22",color:STAGE_COLORS[l.stage]}}>{t.leads.stages[l.stage]}</span>
                <span className="green-text">{fmt(effectiveValue)}{fromEstimate&&<span style={{fontSize:9,opacity:0.6,marginLeft:3}}>est.</span>}</span>
                <div className="list-row-actions"><button className="btn-icon" title={t.common.edit} onClick={()=>openEdit(l)}><Icon name="edit" size={14}/></button><button className="btn-icon danger" title={t.common.delete} onClick={()=>setConfirmDelete(l)}><Icon name="trash" size={14}/></button></div>
              </div>
              );
            })}
          </div>
        )}
      </div>
      {(modal==="add"||modal==="edit")&&(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&tryClose(()=>setModal(null),save)}>
          <div className="modal">
            <div className="modal-header"><div className="modal-title">{modal==="add"?t.leads.addTitle:t.leads.editTitle}</div><button className="btn-icon" title={t.common.close} onClick={()=>tryClose(()=>setModal(null),save)}><Icon name="x"/></button></div>
            <div className="modal-body" onChange={()=>setIsDirty(true)}>
              <div className="form-grid form-grid-2">
                <div className="form-group" style={{gridColumn:"1/-1"}}><label>{t.leads.leadName} *</label><input className="form-input" value={form.name||""} onChange={e=>setForm(p=>({...p,name:e.target.value}))}/></div>
                <div className="form-group"><label>{t.leads.customer}</label><select className="form-select" value={form.customerId||""} onChange={e=>setForm(p=>({...p,customerId:e.target.value}))}><option value="">—</option>{customers.map(c=><option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}</select></div>
                <div className="form-group"><label>{t.leads.value}</label><input className="form-input" type="number" value={form.value||""} onChange={e=>setForm(p=>({...p,value:e.target.value}))}/><div style={{fontSize:11,color:"var(--text-3)",marginTop:4}}>{lang==="fr"?"Valeur manuelle — remplacée par la soumission si disponible":"Manual fallback — overridden by estimate if available"}</div></div>
                <div className="form-group"><label>{t.leads.stage}</label><select className="form-select" value={form.stage||"new"} onChange={e=>setForm(p=>({...p,stage:e.target.value}))}>{LEAD_STAGES.map(s=><option key={s} value={s}>{t.leads.stages[s]}</option>)}</select></div>
                <div className="form-group"><label>{t.leads.service}</label><select className="form-select" value={form.service||""} onChange={e=>setForm(p=>({...p,service:e.target.value}))}>{["Hardscape","Landscape","Excavation","Pool","Other"].map(s=><option key={s}>{s}</option>)}</select></div>
                <div className="form-group"><label>Date</label><input className="form-input" type="date" value={form.date||""} onChange={e=>setForm(p=>({...p,date:e.target.value}))}/></div>
                <div className="form-group" style={{gridColumn:"1/-1"}}><label>{t.leads.notes}</label><textarea className="form-textarea" value={form.notes||""} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>tryClose(()=>setModal(null),save)}>{t.leads.cancel}</button><button className="btn btn-primary" onClick={save}>{t.leads.save}</button></div>
          </div>
        </div>
      )}
      {guard&&<UnsavedChangesGuard t={t} onSave={async()=>{await guard.save();setGuard(null);setIsDirty(false);}} onDiscard={()=>{guard.close();setGuard(null);setIsDirty(false);}} onKeepEditing={()=>setGuard(null)}/>}
      {confirmDelete&&<ConfirmDelete message={`Supprimer le prospect "${confirmDelete.name}"?`} onConfirm={()=>{deleteLead(confirmDelete.id);setConfirmDelete(null);}} onCancel={()=>setConfirmDelete(null)}/>}
    </div>
  );
}

export default Pipeline;
